import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { makeArrayFromLength } from "oolib";
import { _extractAllAnnoBasedDimensionsAsOptions } from "./_extractAllAnnoBasedDimensionsAsOptions";

/**
 * The grouped sub-theme dimension options ("Life Skills & Leadership",
 * "Sentiment Polarity", ...) that the cascade picker folds into its
 * Thematics / Attributes folders (keyed on isAnnoSubTheme + taxonomyRole).
 *
 * Extracted from useGetAllOptionsForDimension1 so that dimension 2 (and any
 * other dimension slot — pie layers, sankey, heatmap Y) can offer the same
 * options; historically only dim1 fetched these, which made every
 * sub-theme × anything chart unbuildable from the second slot.
 *
 * Fetches the rollup parent tags (sub-themes) once via react-query; while
 * loading, returns skeleton placeholder rows ({loading: true}) exactly like
 * dim1 always has.
 */
export const useGroupedSubThemeOptions = () => {
  const allTpls = useGetQueryData("allTpls");
  const { SELF_MANAGED_BASE_CONFIGS, annotationTagsCount } = useGetQueryData("platformConfigs");

  const smTagTypesConfig = SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;
  const { annoRollupOptions } = _extractAllAnnoBasedDimensionsAsOptions({
    allTpls,
    isSelfManagedTenant: SELF_MANAGED_BASE_CONFIGS.enable === true,
    annotationTagsCount,
    smTagTypesConfig,
  });

  // only supports tag type rollup for now. and only paths that are 1 level deep.
  const rollupOptionsSupportedForGrouping = annoRollupOptions.filter(
    (op) => op.rollupType === "tagType" && op.rollupPath.length === 2
  );

  const { data: rollupParentTags, status } = useGetDataQuery({
    enabled: rollupOptionsSupportedForGrouping.length > 0,
    contentTypes: rollupOptionsSupportedForGrouping.map((op) => op.rollupPath[1]),
    projection: {
      _id: 1,
      "main.title": 1,
      "meta.kp_content_type": 1,
      // Needed to split sub-themes into Thematics vs Attributes folders in the
      // cascade picker (self-managed + non-legacy anno mode). Harmless otherwise.
      "meta.taxonomyRole": 1,
    },
  });

  const findRollupOptionFor = (d) =>
    rollupOptionsSupportedForGrouping.find(
      (dd) => dd.rollupPath[1] === d.meta.kp_content_type
    );

  // Builds ONE grouped-rollup (sub-theme) option. The emitted shape is what the
  // aggregation consumes — display/desc are cosmetic; value/valuePath/
  // presetActiveFilters (incl. the legacy filterId) drive the request.
  const buildGroupedRollupOption = (d) => {
    const rollupPath = findRollupOptionFor(d).rollupPath;
    const rolledUpTagTypeDisplay =
      allTpls.find((tpl) => tpl.kp_content_type === rollupPath[0])?.general?.content?.title ||
      rollupPath[0];
    return {
      // Show the SUB-THEME name (this option groups child tags by this
      // sub-theme). The child collection ("Tags") moves to the desc line so it
      // no longer reads "Tags · Tags · Tags" in the Thematics/Attributes folder.
      display: d.main?.title || d._id,
      desc: `${rolledUpTagTypeDisplay}`,
      value: d._id,
      isAnno: true,
      // Stamped so the cascade picker can split Thematics/Attributes on its own
      // (no side-channel threading): isAnnoSubTheme marks this as a sub-theme
      // grouping; taxonomyRole ('attribute' | undefined/other) chooses the folder.
      isAnnoSubTheme: true,
      taxonomyRole: d.meta?.taxonomyRole,
      valuePath: `annotations.tags.${rollupPath[0]}`,
      presetActiveFilters: [
        {
          filterId: "activeFilterFrom_" + "ANNO_TAG__tags.themeTags_from_" + d._id,
          values: [{ value: d._id }],
          target: {
            filterType: "nestedRollupTagType",
            isAnno: true,
            rollupPath,
          },
        },
      ],
    };
  };

  const groupedSubThemeOptions =
    rollupOptionsSupportedForGrouping.length > 0 /** means enabled */
      ? status === "success"
        ? rollupParentTags.data.map(buildGroupedRollupOption)
        : status === "loading"
          ? makeArrayFromLength(3).map(() => ({ loading: true }))
          : []
      : [];

  return groupedSubThemeOptions;
};
